import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { Input } from '@/core/Input';
import type { Player } from '@/gameplay/Player';
import type { Faction } from '@/gameplay/GameState';
import type { CollisionWorld } from '@/world/collision';
import { bodies as worldBodies, shoveActorFromBox, type ActorBody, type BodyRegistry } from '@/world/bodies';
import { buildVehicle, VEHICLES, type VehicleKind } from '@/meshkit/vehicles/cars';
import { advanceVehicle } from '@/gameplay/vehicleMotion';

export type VehicleState = 'parked' | 'driving' | 'burned';

export interface VehicleConfig {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly position: THREE.Vector2;
  readonly rotation?: number;
  readonly enterRadius?: number;
  readonly maxHealth?: number;
}

export interface VehicleSnapshot {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly state: VehicleState;
  readonly position: THREE.Vector2;
  readonly rotation: number;
  readonly speed: number;
  readonly health: number;
}

export const VEHICLE = {
  enterRadius: 1.6,
  acceleration: 7.8,
  reverseAcceleration: 4.6,
  brake: 12,
  drag: 2.2,
  maxSpeed: 9.2,
  maxReverseSpeed: 3.4,
  turnRate: 2.65,
  maxHealth: 100,
  collisionSkin: 0.92,
  damageSpeedThreshold: 1.2,
  entryGraceSeconds: 0.35,
  /** Ruimte tussen de carrosserie en waar de bestuurder neerkomt. */
  exitClearance: 0.78,
  /**
   * Vanaf hier gieren de banden. Uitgedrukt als stuuruitslag maal snelheid, want alleen hard
   * sturen bij stilstand hoort niets te doen.
   */
  skidThreshold: 4.2,
  maxStepDistance: 0.08,
  /** Vaart die je overhoudt na een schampbotsing. Genoeg om door te rijden, hoorbaar duur. */
  scrapeSpeedKeep: 0.86,
} as const;

/**
 * Aanrijden.
 *
 * Boven de drempel gaat iedereen te voet neer — vijanden, je eigen soldaten en Luca zelf.
 * Eronder duw je iemand alleen opzij. Zo kun je niet stapvoets een moord plegen, en blijf je
 * ook niet steken achter een voetganger die toevallig voor je bumper staat.
 *
 * De drempel ligt op iets minder dan de helft van de topsnelheid (9,2): hard genoeg dat het
 * een keuze is, laag genoeg dat een echte aanrijding altijd raak is.
 */
export const RAM = {
  lethalSpeed: 4,
  /** Ruim boven elke levensbalk in het spel: een auto vol op je lijf is geen schaafwond. */
  lethalDamage: 500,
  /** Hoeveel vaart de auto verliest bij een aanrijding. */
  speedKeep: 0.82,
} as const;

/** Personages hebben een faction; het botsingsregister eist die niet, dus vandaar de val-terug. */
function factionOf(actor: ActorBody): Faction {
  return (actor as ActorBody & { faction?: Faction }).faction ?? 'neutral';
}

/**
 * Rijd de mensen aan die onder deze auto liggen.
 *
 * Wordt zowel door de bestuurde auto als door het AI-verkeer aangeroepen, want een
 * taxichauffeur die je overrijdt hoort net zo dodelijk te zijn als jij.
 */
export function runOverActors(
  vehicle: Pick<Vehicle, 'id' | 'position' | 'rotation' | 'collisionHalfWidth' | 'collisionHalfLength'>,
  speed: number,
  bodies: BodyRegistry,
  driver: ActorBody | null,
): ActorBody[] {
  const hits = bodies.actorsInBox(
    vehicle.position.x,
    vehicle.position.y,
    vehicle.rotation,
    vehicle.collisionHalfWidth,
    vehicle.collisionHalfLength,
    driver ?? undefined,
  );
  if (hits.length === 0) return hits;

  const lethal = Math.abs(speed) >= RAM.lethalSpeed;
  for (const actor of hits) {
    if (!lethal) {
      shoveActorFromBox(
        actor,
        vehicle.position.x,
        vehicle.position.y,
        vehicle.rotation,
        vehicle.collisionHalfWidth,
        vehicle.collisionHalfLength,
      );
      continue;
    }
    actor.damage(RAM.lethalDamage);
    bus.emit('vehicle:ranOver', {
      id: vehicle.id,
      x: actor.position.x,
      z: actor.position.y,
      speed: Math.abs(speed),
      faction: factionOf(actor),
    });
  }
  return lethal ? hits : [];
}

export class Vehicle {
  readonly id: string;
  readonly kind: VehicleKind;
  readonly object: THREE.Group;
  readonly position = new THREE.Vector2();

  rotation: number;
  speed = 0;
  health: number;
  state: VehicleState = 'parked';

  readonly #enterRadius: number;
  readonly #halfWidth: number;
  readonly #halfLength: number;
  readonly #maxHealth: number;
  #driver: Player | null = null;
  #enteredBeforeNextUpdate = false;
  #entryGrace = 0;

  constructor(config: VehicleConfig) {
    this.id = config.id;
    this.kind = config.kind;
    this.position.copy(config.position);
    this.rotation = config.rotation ?? 0;
    this.object = buildVehicle(config.kind);
    this.object.position.set(this.position.x, 0, this.position.y);
    this.object.rotation.y = this.rotation;
    this.#enterRadius = config.enterRadius ?? VEHICLE.enterRadius;
    this.#maxHealth = config.maxHealth ?? VEHICLE.maxHealth;
    this.health = this.#maxHealth;

    const spec = VEHICLES[config.kind];
    this.#halfWidth = spec.width * VEHICLE.collisionSkin / 2;
    this.#halfLength = spec.length * VEHICLE.collisionSkin / 2;
  }

  get occupied(): boolean {
    return this.#driver !== null;
  }

  get collisionHalfWidth(): number {
    return this.#halfWidth;
  }

  get collisionHalfLength(): number {
    return this.#halfLength;
  }

  get velocity(): THREE.Vector2 {
    return new THREE.Vector2(Math.sin(this.rotation), Math.cos(this.rotation)).multiplyScalar(this.speed);
  }

  canEnter(player: Player): boolean {
    return this.state !== 'burned' && !this.occupied && player.position.distanceTo(this.position) <= this.#enterRadius;
  }

  update(dt: number, input: Input, world: CollisionWorld, player: Player, vehicles: readonly Vehicle[]): void {
    if (this.state === 'burned') return;

    if (this.#driver) {
      const enteredThisFrame = this.#enteredBeforeNextUpdate;
      this.#enteredBeforeNextUpdate = false;
      this.#drivingStep(dt, input, world, vehicles);
      this.#entryGrace = Math.max(0, this.#entryGrace - dt);
      this.#syncDriver();
      if (!enteredThisFrame && input.wasPressed('e')) this.exitDriver(player, world);
      return;
    }

    if (input.wasPressed('e') && this.canEnter(player)) {
      this.enterDriver(player);
      this.#enteredBeforeNextUpdate = false;
    }
  }

  enterDriver(player: Player): void {
    if (!this.canEnter(player)) return;
    this.#driver = player;
    this.state = 'driving';
    this.speed = 0;
    this.#enteredBeforeNextUpdate = true;
    this.#entryGrace = VEHICLE.entryGraceSeconds;
    player.object.visible = false;
    player.velocity.set(0, 0);
    this.#syncDriver();
    bus.emit('vehicle:entered', { id: this.id, kind: this.kind });
  }

  exitDriver(player: Player, world: CollisionWorld): boolean {
    if (this.#driver !== player) return false;
    const exit = this.#exitPoint(world, player.radius);
    if (!exit) return false;
    this.#driver = null;
    this.#enteredBeforeNextUpdate = false;
    this.#entryGrace = 0;
    this.state = 'parked';
    player.position.copy(exit);
    player.object.visible = true;
    player.object.position.set(exit.x, 0, exit.y);
    bus.emit('vehicle:exited', { id: this.id, kind: this.kind });
    return true;
  }

  damage(amount: number, reason = 'unknown', speed = Math.abs(this.speed)): void {
    if (this.state === 'burned') return;
    this.health = Math.max(0, this.health - amount);
    bus.emit('vehicle:damaged', {
      id: this.id,
      kind: this.kind,
      amount,
      reason,
      speed,
      x: this.position.x,
      z: this.position.y,
      healthLeft: this.health,
    });
    if (this.health === 0) {
      this.state = 'burned';
      this.speed = 0;
      if (this.#driver) {
        this.#driver.object.visible = true;
        this.#driver = null;
      }
      bus.emit('vehicle:burned', { id: this.id, kind: this.kind });
    }
  }

  snapshot(): VehicleSnapshot {
    return {
      id: this.id,
      kind: this.kind,
      state: this.state,
      position: this.position.clone(),
      rotation: this.rotation,
      speed: this.speed,
      health: this.health,
    };
  }

  #drivingStep(dt: number, input: Input, world: CollisionWorld, vehicles: readonly Vehicle[]): void {
    const throttle = (input.isDown('w') || input.isDown('arrowup') ? 1 : 0)
      - (input.isDown('s') || input.isDown('arrowdown') ? 1 : 0);
    const steering = (input.isDown('a') || input.isDown('arrowleft') ? 1 : 0)
      - (input.isDown('d') || input.isDown('arrowright') ? 1 : 0);
    const braking = input.isDown(' ');

    if (throttle > 0) this.speed += VEHICLE.acceleration * dt;
    if (throttle < 0) this.speed -= VEHICLE.reverseAcceleration * dt;
    if (braking) this.speed = moveToward(this.speed, 0, VEHICLE.brake * dt);
    if (throttle === 0 && !braking) this.speed = moveToward(this.speed, 0, VEHICLE.drag * dt);
    this.speed = THREE.MathUtils.clamp(this.speed, -VEHICLE.maxReverseSpeed, VEHICLE.maxSpeed);

    const turnStrength = THREE.MathUtils.clamp(Math.abs(this.speed) / 3.5, 0, 1);
    this.rotation += steering * VEHICLE.turnRate * turnStrength * Math.sign(this.speed || 1) * dt;

    const skid = Math.abs(steering) * Math.abs(this.speed);
    if (skid > VEHICLE.skidThreshold) {
      bus.emit('vehicle:skid', {
        id: this.id,
        x: this.position.x,
        z: this.position.y,
        severity: THREE.MathUtils.clamp((skid - VEHICLE.skidThreshold) / VEHICLE.maxSpeed, 0, 1),
      });
    }

    if (Math.abs(this.speed) < 0.001) {
      this.speed = 0;
      this.object.position.set(this.position.x, 0, this.position.y);
      this.object.rotation.y = this.rotation;
      return;
    }

    const blockers = vehicles.filter((vehicle) => vehicle !== this && vehicle.state !== 'burned');
    const impactSpeed = Math.abs(this.speed);
    const advance = advanceVehicle(this, this.speed * dt, this.#halfWidth, this.#halfLength, world, blockers);

    if (advance.blocked) {
      if (advance.slid) {
        // Langs iets heen geschuurd: vaart kost het, maar je blijft rijden. Dat scheelt het
        // volledig stilvallen zodra je een lantaarnpaal aantikt.
        this.speed *= VEHICLE.scrapeSpeedKeep;
      } else {
        this.speed = -this.speed * 0.18;
        if (this.#entryGrace <= 0 && impactSpeed >= VEHICLE.damageSpeedThreshold) {
          this.damage(8, 'collision', impactSpeed);
        }
        this.object.position.set(this.position.x, 0, this.position.y);
        this.object.rotation.y = this.rotation;
        return;
      }
    }

    this.object.position.set(this.position.x, 0, this.position.y);
    this.object.rotation.y = this.rotation;

    const killed = runOverActors(this, this.speed, worldBodies, this.#driver);
    if (killed.length > 0) this.speed *= RAM.speedKeep;
  }

  #syncDriver(): void {
    if (!this.#driver) return;
    this.#driver.position.copy(this.position);
    this.#driver.velocity.set(0, 0);
    this.#driver.object.position.set(this.position.x, 0, this.position.y);
  }

  /**
   * Waar stapt de bestuurder uit?
   *
   * Dit probeerde precies één kant. Stond die tegen een muur — en dat is in smalle
   * havenstraatjes eerder regel dan uitzondering — dan kon je de auto helemaal niet meer uit.
   * Nu worden beide zijden geprobeerd en daarna voor en achter; de eerste vrije plek wint.
   * Is alles bezet, dan blijf je zitten, maar dat is nu zeldzaam in plaats van alledaags.
   */
  #exitPoint(world: CollisionWorld, radius: number): THREE.Vector2 | null {
    const side = new THREE.Vector2(Math.cos(this.rotation), -Math.sin(this.rotation));
    const forward = new THREE.Vector2(Math.sin(this.rotation), Math.cos(this.rotation));
    const sideOffset = this.#halfWidth + VEHICLE.exitClearance;
    const endOffset = this.#halfLength + VEHICLE.exitClearance;

    const candidates = [
      this.position.clone().addScaledVector(side, sideOffset),
      this.position.clone().addScaledVector(side, -sideOffset),
      this.position.clone().addScaledVector(forward, -endOffset),
      this.position.clone().addScaledVector(forward, endOffset),
    ];

    for (const candidate of candidates) {
      if (boxOverlapsWorld(candidate.x, candidate.y, radius, radius, world, [])) continue;
      return candidate;
    }

    return null;
  }
}

export function boxOverlapsWorld(
  x: number,
  z: number,
  halfX: number,
  halfZ: number,
  world: CollisionWorld,
  vehicles: readonly Vehicle[],
  rotation = 0,
): boolean {
  const queryRadius = Math.hypot(halfX, halfZ);
  for (const obstacle of world.obstaclesNear(x, z, queryRadius)) {
    if (rotatedBoxOverlap(x, z, halfX, halfZ, rotation, obstacle.x, obstacle.z, obstacle.halfX, obstacle.halfZ, 0)) {
      return true;
    }
  }
  for (const vehicle of vehicles) {
    if (
      rotatedBoxOverlap(
        x,
        z,
        halfX,
        halfZ,
        rotation,
        vehicle.position.x,
        vehicle.position.y,
        vehicle.collisionHalfWidth,
        vehicle.collisionHalfLength,
        vehicle.rotation,
      )
    ) {
      return true;
    }
  }
  return false;
}

export function rotatedBoxOverlap(
  ax: number,
  az: number,
  ahx: number,
  ahz: number,
  aRotation: number,
  bx: number,
  bz: number,
  bhx: number,
  bhz: number,
  bRotation: number,
): boolean {
  const aAxisX = Math.cos(aRotation);
  const aAxisZ = Math.sin(aRotation);
  const bAxisX = Math.cos(bRotation);
  const bAxisZ = Math.sin(bRotation);
  const axes = [
    { x: aAxisX, z: -aAxisZ },
    { x: aAxisZ, z: aAxisX },
    { x: bAxisX, z: -bAxisZ },
    { x: bAxisZ, z: bAxisX },
  ];
  const dx = bx - ax;
  const dz = bz - az;

  for (const axis of axes) {
    const distance = Math.abs(dx * axis.x + dz * axis.z);
    const aProjection = boxProjectionRadius(axis.x, axis.z, aRotation, ahx, ahz);
    const bProjection = boxProjectionRadius(axis.x, axis.z, bRotation, bhx, bhz);
    if (distance >= aProjection + bProjection) return false;
  }

  return true;
}

function boxProjectionRadius(axisX: number, axisZ: number, rotation: number, halfX: number, halfZ: number): number {
  const localXx = Math.cos(rotation);
  const localXz = -Math.sin(rotation);
  const localZx = Math.sin(rotation);
  const localZz = Math.cos(rotation);
  return (
    halfX * Math.abs(axisX * localXx + axisZ * localXz)
    + halfZ * Math.abs(axisX * localZx + axisZ * localZz)
  );
}

function moveToward(value: number, target: number, amount: number): number {
  if (value < target) return Math.min(target, value + amount);
  if (value > target) return Math.max(target, value - amount);
  return value;
}
